import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

/**
 * Hook that pre-fetches FLEHS API token after login.
 * This warms the MSAL cache so subsequent API calls are faster.
 * Also detects stale sessions early and clears cache if needed.
 */
export function useFlehsTokenAcquisition() {
  // instance: MSAL client for token operations
  // accounts: Array of signed-in accounts (empty = not logged in)
  // inProgress: Current MSAL operation state ("none" when idle)
  const { instance, accounts, inProgress } = useMsal();

  useEffect(() => {
    // Only run when: user is logged in AND no MSAL operation in progress
    if (accounts.length === 0 || inProgress !== "none") {
      return;
    }

    const acquireFlehsToken = async () => {
      // FLEHS API scope - identifies which API we're requesting access to
      const scopes = ["api://5b9d1c95-aa2d-4545-af3f-72cc38b875f4/Flehs.User"];

      try {
        // Pre-fetch token silently to warm the cache
        // If successful, subsequent useAuthenticatedApiClient calls will be instant
        await instance.acquireTokenSilent({
          scopes,
          account: accounts[0],
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // Session is stale - clear cache so app recognizes user as logged out
          // Route fallbacks will show "please sign in" UI
          instance.setActiveAccount(null);
          instance.clearCache();
        } else {
          // Log non-auth errors for debugging (network issues, etc.)
          console.error("Failed to acquire FLEHS token:", error);
        }
      }
    };

    // Execute token acquisition
    acquireFlehsToken();
  }, [instance, accounts, inProgress]);
}
